import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Form, Row } from "react-bootstrap";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import { Trans, useTranslation } from "react-i18next";
import {
  getSymbolRequestBrokerages,
  requestBrokerageSymbols,
} from "../../actions/setting-action";
import "./SymbolRequestPanel.scss";

const MAX_SYMBOLS_PER_HOUR = 5;

const normalizeSymbols = (value) =>
  Array.from(
    new Set(
      String(value || "")
        .split(/[,\s]+/)
        .map((symbol) => symbol.trim().replace(/^\$/, "").toUpperCase())
        .filter(Boolean)
    )
  );

const groupResultItems = (items) => {
  const grouped = {};
  (items || []).forEach((item) => {
    const symbol = item.symbol || item.requested_symbol;
    if (!symbol) {
      return;
    }

    if (!grouped[symbol]) {
      grouped[symbol] = {
        symbol,
        brokerages: [],
      };
    }

    const brokerage = item.exchange_name || (item.exchange_id !== undefined ? `E#${item.exchange_id}` : "");
    if (brokerage && !grouped[symbol].brokerages.includes(brokerage)) {
      grouped[symbol].brokerages.push(brokerage);
    }
  });

  return Object.values(grouped);
};

const SymbolRequestPanel = (props) => {
  const { getSymbolRequestBrokerages, requestBrokerageSymbols } = props;
  const { t } = useTranslation();
  const [symbols, setSymbols] = useState("");
  const [brokerages, setBrokerages] = useState([]);
  const [selectedBrokerageIds, setSelectedBrokerageIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    setLoading(true);
    getSymbolRequestBrokerages()
      .then((data) => {
        const list = data.brokerages || [];
        setBrokerages(list);
        //setSelectedBrokerageIds(list.map((item) => item.exchange_id));
        setNote(data.note || "");
      })
      .catch((err) => {
        setError(String(err));
      })
      .finally(() => setLoading(false));
  }, [getSymbolRequestBrokerages]);

  const selectedCount = selectedBrokerageIds.length;
  const allSelected = brokerages.length > 0 && selectedCount === brokerages.length;
  const requestedSymbols = useMemo(() => normalizeSymbols(symbols), [symbols]);
  const exceedsSymbolLimit = requestedSymbols.length > MAX_SYMBOLS_PER_HOUR;
  const canSubmit =
    symbols.trim().length > 0 && selectedCount > 0 && !submitting && !exceedsSymbolLimit;
  const summaryText = useMemo(() => {
    if (!result || !result.summary) {
      return "";
    }

    const summary = result.summary;
    const failedSymbolCount = new Set([
      ...(result.invalid_symbols || []),
      ...(result.failed || []).map((item) => item.symbol || item.requested_symbol).filter(Boolean),
    ]).size;

    return `${summary.created_count || 0} ${t("created")}, ${
      summary.existing_count || 0
    } ${t("existing")}, ${failedSymbolCount} ${t("could-not-add")}`;
  }, [result, t]);

  const failedGroups = useMemo(() => {
    if (!result) {
      return [];
    }

    const groups = groupResultItems(result.failed);
    (result.invalid_symbols || []).forEach((symbol) => {
      if (!groups.some((item) => item.symbol === symbol)) {
        groups.push({ symbol, brokerages: [] });
      }
    });

    return groups;
  }, [result]);

  const formatRequestError = (err) => {
    if (err && typeof err === "object") {
      if (err.error === "symbol_request_limit_exceeded") {
        return t("symbol-request-limit-error");
      }

      return err.message || String(err);
    }

    const message = String(err);
    if (message.includes("maximum of 5 symbols")) {
      return t("symbol-request-limit-error");
    }

    return message;
  };

  const handleSymbolsChange = (event) => {
    setSymbols(event.target.value);
    setError("");
    setResult(null);
  };

  const toggleBrokerage = (exchangeId) => {
    setError("");
    setResult(null);
    setSelectedBrokerageIds((ids) => {
      if (ids.includes(exchangeId)) {
        return ids.filter((id) => id !== exchangeId);
      }

      return [...ids, exchangeId];
    });
  };

  const toggleAll = () => {
    setError("");
    setResult(null);
    if (allSelected) {
      setSelectedBrokerageIds([]);
      return;
    }

    setSelectedBrokerageIds(brokerages.map((item) => item.exchange_id));
  };

  const submit = (event) => {
    event.preventDefault();
    setError("");
    setResult(null);

    if (!symbols.trim()) {
      toast.error(t("symbols-required"));
      return;
    }

    if (exceedsSymbolLimit) {
      return;
    }

    if (selectedBrokerageIds.length === 0) {
      toast.error(t("brokerages-required"));
      return;
    }

    setSubmitting(true);
    requestBrokerageSymbols({
      symbols,
      exchange_ids: selectedBrokerageIds,
    })
      .then((data) => {
        setResult(data);
        setNote(data.note || note);
        if ((data.invalid_symbols || []).length > 0 || (data.failed || []).length > 0) {
          toast.warn(t("symbols-added-with-invalid"));
        }
      })
      .catch((err) => {
        setError(formatRequestError(err));
      })
      .finally(() => setSubmitting(false));
  };

  const renderBrokerageList = () => {
    if (loading) {
      return <div className="symbol-request-empty">{t("loading")}</div>;
    }

    if (brokerages.length === 0) {
      return <div className="symbol-request-empty">{t("no-connected-brokerages")}</div>;
    }

    return (
      <>
        <button
          type="button"
          className="symbol-request-select-all"
          onClick={()=>toggleAll()}
        >
          {allSelected ? t("clear-all") : t("select-all")}
        </button>
        <div className="symbol-request-brokerages">
          {brokerages.map((brokerage) => {
            const checked = selectedBrokerageIds.includes(brokerage.exchange_id);
            return (
              <label
                className={`symbol-request-brokerage ${checked ? "active" : ""}`}
                key={brokerage.exchange_id}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleBrokerage(brokerage.exchange_id)}
                />
                <img src={brokerage.image} alt="" />
                <span>{brokerage.name}</span>
              </label>
            );
          })}
        </div>
      </>
    );
  };

  const renderGroupedList = (items, emptyText) => {
    const groups = groupResultItems(items);
    if (groups.length === 0) {
      return <span className="symbol-request-muted">{emptyText || "-"}</span>;
    }

    return (
      <div className="symbol-request-grouped-list">
        {groups.map((item) => (
          <div key={item.symbol}>
            <strong>{item.symbol}</strong>
            <span>{item.brokerages.join(", ")}</span>
          </div>
        ))}
      </div>
    );
  };

  const renderFailureList = (items) => {
    if (!items || items.length === 0) {
      return <span className="symbol-request-muted">-</span>;
    }

    return (
      <div className="symbol-request-failures">
        {items.map((item) => (
          <div key={item.symbol}>
            <strong>{item.symbol}</strong>
            {item.brokerages.length > 0 ? <span>{item.brokerages.join(", ")}</span> : null}
            <small>{t("symbol-request-failed-help")}</small>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="setting-general-content">
    <div className="col-sm-10 setting-right-col">
      <div className="symbol-request-panel">
        <div className="symbol-request-header">
          <h4>{t("request-symbols")}</h4>
          {note ? <p>{note}</p> : null}
        </div>

        <Alert variant="warning" className="symbol-request-limit-note">
          <strong>{t("symbol-request-hourly-limit")}</strong>{" "}
          <Trans i18nKey="symbol-request-limit-note">
            <a className="symbol-request-email-link" href="mailto:admin@stockhero.ai">
              admin@stockhero.ai
            </a>
          </Trans>
        </Alert>

        <Form onSubmit={submit}>
          <Form.Group as={Row} className="symbol-request-symbols-row">
            <Form.Label column sm={3}>
              {t("symbols")}
            </Form.Label>
            <Col sm={9}>
              <Form.Control
                type="text"
                className="input-text symbol-request-input"
                value={symbols}
                placeholder="AAPL, MSFT, TSLA"
                onChange={handleSymbolsChange}
              />
              {exceedsSymbolLimit ? (
                <div className="symbol-request-field-error">
                  {t("symbol-request-limit-error")}
                </div>
              ) : null}
            </Col>
          </Form.Group>

          <Form.Group as={Row} className="symbol-request-brokerages-row">
            <Form.Label column sm={3}>
              {t("brokerages")}
            </Form.Label>
            <Col sm={9}>{renderBrokerageList()}</Col>
          </Form.Group>

          {error ? (
            <Alert variant="danger" className="symbol-request-alert">
              {error}
            </Alert>
          ) : null}

          <div className="symbol-request-actions">
            <Button type="submit" className="blue-button" disabled={!canSubmit}>
              {submitting ? t("updating") : t("submit")}
            </Button>
          </div>
        </Form>

        {result ? (
          <div className="symbol-request-result">
            <Alert variant={failedGroups.length > 0 ? "warning" : "success"}>
              {summaryText}
            </Alert>
            <div className="symbol-request-result-row">
              <strong>{t("created")}</strong>
              {renderGroupedList(result.created, t("no-new-symbols-added"))}
            </div>
            <div className="symbol-request-result-row">
              <strong>{t("existing")}</strong>
              {renderGroupedList(result.existing)}
            </div>
            {failedGroups.length > 0 ? (
              <div className="symbol-request-result-row">
                <strong>{t("could-not-add")}</strong>
                {renderFailureList(failedGroups)}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
    </div>
  );
};

export default connect(null, {
  getSymbolRequestBrokerages,
  requestBrokerageSymbols,
})(SymbolRequestPanel);
